import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { hiddenActions, useDispatchHidden, useHidden } from "contexts/hidden";
import { useDispatchWindows, windowsActions } from "contexts/windows";
import { CORE_WINDOW_IDS } from "contexts/windows/constants";
import { coordinatesActions, selectCoordinates } from "features/coordinates";
import { measureActions, selectMeasureEntities, selectMeasureVisible } from "features/measure/measureSlice";
import { clippingOutlineLaserActions, selectOutlineLasers } from "features/outlineLaser/outlineLaserSlice";
import { pointLineActions, selectPointLines } from "features/pointLine/pointLineSlice";
import { renderActions, selectClippingPlanes } from "features/render";
import { cloneClippingPlanes } from "features/sectionPlane/sectionPlaneUndoRedo";
import { useEnterToolMode } from "features/toolMode/useEnterToolMode";
import { undoRedoActions } from "features/undoRedo";
import { collectPdfHiddenIds, partitionHiddenIds, useObjectTreeHidden } from "hooks/useObjectTreeHidden";
import { ProcoreToolMode } from "slices/procoreUx";
import { restoreUndoIds, snapshotUndoIds } from "utils/undoIds";

export type ModificationCategory = {
    id: string;
    label: string;
    count: number;
    isEnabled: boolean;
    onToggle: (() => void) | undefined;
    onClear: () => void;
    onActivate: () => void;
};

/**
 * Returns the currently active modification categories and actions to toggle or clear each one.
 * A category is included only when it has at least one active modification.
 */
export function useViewModificationCategories(): ModificationCategory[] {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const dispatchHidden = useDispatchHidden();
    const dispatchWindows = useDispatchWindows();
    const enterToolMode = useEnterToolMode();

    // Objects Hidden — PDFs/2D sheets stay hidden in 3D, but are not counted here.
    const hidden = useHidden();
    const { objectTreeHiddenCount, objectTreeSnapshotCount, pdfObjectIds } = useObjectTreeHidden();
    const hiddenSnapshot = hidden.toggleSnapshot;
    const hiddenCount = objectTreeHiddenCount;
    // Category stays visible even when toggled off (snapshot has items but idArr is empty)
    const isHiddenCategoryActive = hiddenCount > 0 || objectTreeSnapshotCount > 0;
    // Sectioning
    const clippingPlanes = useAppSelector(selectClippingPlanes);
    const sectionCount = clippingPlanes.planes.length;
    const isSectioningActive = sectionCount > 0;

    // Measurements — aggregate all measurement types
    const coordinates = useAppSelector(selectCoordinates);
    const lasers = useAppSelector(selectOutlineLasers);
    const pointLines = useAppSelector(selectPointLines);
    const measureEntities = useAppSelector(selectMeasureEntities);
    const measureVisible = useAppSelector(selectMeasureVisible);

    const coordinatesCount = coordinates.length;
    const lasersCount = lasers.length;
    const pointLinesCount = pointLines.filter((pl) => pl.points.length > 0).length;
    const dimensionsCount = measureEntities.filter((set) => set.length > 0).length;
    const measurementCount = coordinatesCount + lasersCount + pointLinesCount + dimensionsCount;
    const isMeasurementsActive = measurementCount > 0;

    const handleToggleHidden = useCallback(() => {
        if (!pdfObjectIds) {
            return;
        }
        const beforeIds = snapshotUndoIds(hidden.ids);
        const beforeSnapshot = snapshotUndoIds(hiddenSnapshot);

        const { objectTreeIds, pdfIds } = partitionHiddenIds(hidden.ids, pdfObjectIds);
        if (hiddenCount > 0) {
            dispatchHidden(hiddenActions.setToggleSnapshot(objectTreeIds));
            dispatchHidden(hiddenActions.setIds(pdfIds));
            dispatch(
                undoRedoActions.push({
                    description: "Toggle hidden objects off",
                    undoCallback: () => {
                        dispatchHidden(hiddenActions.setToggleSnapshot(restoreUndoIds(beforeSnapshot)));
                        dispatchHidden(hiddenActions.setIds(restoreUndoIds(beforeIds)));
                    },
                    redoCallback: () => {
                        const restored = partitionHiddenIds(restoreUndoIds(beforeIds), pdfObjectIds);
                        dispatchHidden(hiddenActions.setToggleSnapshot(restored.objectTreeIds));
                        dispatchHidden(hiddenActions.setIds(restored.pdfIds));
                    },
                }),
            );
        } else {
            const { objectTreeIds: snapshotObjectTreeIds } = partitionHiddenIds(hiddenSnapshot, pdfObjectIds);
            dispatchHidden(hiddenActions.add(snapshotObjectTreeIds));
            dispatch(
                undoRedoActions.push({
                    description: "Toggle hidden objects on",
                    undoCallback: () => {
                        dispatchHidden(hiddenActions.setToggleSnapshot(restoreUndoIds(beforeSnapshot)));
                        dispatchHidden(hiddenActions.setIds(restoreUndoIds(beforeIds)));
                    },
                    redoCallback: () => {
                        dispatchHidden(hiddenActions.add(snapshotObjectTreeIds));
                    },
                }),
            );
        }
    }, [pdfObjectIds, hidden.ids, hiddenSnapshot, hiddenCount, dispatchHidden, dispatch]);

    const handleClearHidden = useCallback(() => {
        if (!pdfObjectIds) {
            return;
        }
        const pdfIds = collectPdfHiddenIds(hidden.ids, pdfObjectIds);
        const pdfSnapshotIds = collectPdfHiddenIds(hiddenSnapshot, pdfObjectIds);
        const beforeIds = snapshotUndoIds(hidden.ids);
        const beforeSnapshot = snapshotUndoIds(hiddenSnapshot);
        dispatchHidden(hiddenActions.setToggleSnapshot(pdfSnapshotIds));
        dispatchHidden(hiddenActions.setIds(pdfIds));
        dispatch(
            undoRedoActions.push({
                description: "Clear hidden objects",
                undoCallback: () => {
                    dispatchHidden(hiddenActions.setToggleSnapshot(restoreUndoIds(beforeSnapshot)));
                    dispatchHidden(hiddenActions.setIds(restoreUndoIds(beforeIds)));
                },
                redoCallback: () => {
                    dispatchHidden(
                        hiddenActions.setToggleSnapshot(
                            collectPdfHiddenIds(restoreUndoIds(beforeSnapshot), pdfObjectIds),
                        ),
                    );
                    dispatchHidden(hiddenActions.setIds(collectPdfHiddenIds(restoreUndoIds(beforeIds), pdfObjectIds)));
                },
            }),
        );
    }, [pdfObjectIds, hidden.ids, hiddenSnapshot, dispatchHidden, dispatch]);

    const handleToggleSectioning = useCallback(() => {
        const before = { planes: cloneClippingPlanes(clippingPlanes.planes), enabled: clippingPlanes.enabled };
        dispatch(
            renderActions.setClippingPlanes({
                ...clippingPlanes,
                enabled: !clippingPlanes.enabled,
            }),
        );
        dispatch(
            undoRedoActions.push({
                description: "Toggle sectioning",
                undoCallback: () => dispatch(renderActions.setClippingPlanes(before)),
                redoCallback: () => dispatch(renderActions.setClippingPlanes({ ...before, enabled: !before.enabled })),
            }),
        );
    }, [dispatch, clippingPlanes]);

    const handleClearSectioning = useCallback(() => {
        const before = { planes: cloneClippingPlanes(clippingPlanes.planes), enabled: clippingPlanes.enabled };
        dispatch(renderActions.setClippingPlanes({ planes: [], enabled: false }));
        dispatch(
            undoRedoActions.push({
                description: "Clear sectioning",
                undoCallback: () => dispatch(renderActions.setClippingPlanes(before)),
                redoCallback: () => dispatch(renderActions.setClippingPlanes({ planes: [], enabled: false })),
            }),
        );
    }, [dispatch, clippingPlanes]);

    const handleToggleMeasurements = useCallback(() => {
        const before = measureVisible;
        dispatch(measureActions.setVisible(!measureVisible));
        dispatch(
            undoRedoActions.push({
                description: "Toggle measurements",
                undoCallback: () => dispatch(measureActions.setVisible(before)),
                redoCallback: () => dispatch(measureActions.setVisible(!before)),
            }),
        );
    }, [dispatch, measureVisible]);

    const handleClearMeasurements = useCallback(() => {
        const beforeCoordinates = coordinates;
        const beforeLasers = lasers;
        const beforePointLines = pointLines;
        const beforeMeasureEntities = measureEntities.map((set) => structuredClone(set));
        dispatch(coordinatesActions.clear());
        dispatch(clippingOutlineLaserActions.clear());
        dispatch(pointLineActions.clear());
        dispatch(measureActions.clear());
        dispatch(
            undoRedoActions.push({
                description: "Clear measurements",
                undoCallback: () => {
                    dispatch(coordinatesActions.setCoordinates(beforeCoordinates));
                    dispatch(clippingOutlineLaserActions.setLasers(beforeLasers));
                    dispatch(pointLineActions.setPointLines(beforePointLines));
                    if (beforeMeasureEntities.length === 1) {
                        dispatch(measureActions.setSelectedEntities(beforeMeasureEntities[0]));
                    } else {
                        beforeMeasureEntities.forEach((entities, i) =>
                            dispatch(measureActions.setMeasureSet({ idx: i, entities })),
                        );
                    }
                },
                redoCallback: () => {
                    dispatch(coordinatesActions.clear());
                    dispatch(clippingOutlineLaserActions.clear());
                    dispatch(pointLineActions.clear());
                    dispatch(measureActions.clear());
                },
            }),
        );
    }, [dispatch, coordinates, lasers, pointLines, measureEntities]);

    const handleActivateObjectTree = useCallback(() => {
        dispatchWindows(windowsActions.toggleWindow(CORE_WINDOW_IDS.OBJECT_TREE));
    }, [dispatchWindows]);

    const handleActivateSection = useCallback(() => {
        enterToolMode({ mode: ProcoreToolMode.Section });
    }, [enterToolMode]);

    const handleActivateMeasure = useCallback(() => {
        enterToolMode({ mode: ProcoreToolMode.Measure });
    }, [enterToolMode]);

    return useMemo(() => {
        const categories: ModificationCategory[] = [];

        if (isHiddenCategoryActive) {
            categories.push({
                id: "objects-hidden",
                label: t("viewModifications.objectsHidden"),
                count: hiddenCount > 0 ? hiddenCount : objectTreeSnapshotCount,
                isEnabled: hiddenCount > 0,
                onToggle: handleToggleHidden,
                onClear: handleClearHidden,
                onActivate: handleActivateObjectTree,
            });
        }

        if (isSectioningActive) {
            categories.push({
                id: "sectioning",
                label: t("viewModifications.sectioning"),
                count: sectionCount,
                isEnabled: clippingPlanes.enabled,
                onToggle: handleToggleSectioning,
                onClear: handleClearSectioning,
                onActivate: handleActivateSection,
            });
        }

        if (isMeasurementsActive) {
            categories.push({
                id: "measurements",
                label: t("viewModifications.measurements"),
                count: measurementCount,
                isEnabled: measureVisible,
                onToggle: handleToggleMeasurements,
                onClear: handleClearMeasurements,
                onActivate: handleActivateMeasure,
            });
        }

        return categories;
    }, [
        isHiddenCategoryActive,
        hiddenCount,
        objectTreeSnapshotCount,
        handleToggleHidden,
        handleClearHidden,
        handleActivateObjectTree,
        isSectioningActive,
        sectionCount,
        clippingPlanes.enabled,
        handleToggleSectioning,
        handleClearSectioning,
        handleActivateSection,
        isMeasurementsActive,
        measurementCount,
        measureVisible,
        handleToggleMeasurements,
        handleClearMeasurements,
        handleActivateMeasure,
        t,
    ]);
}
